//mushroom-tasks-page.tsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { StripedDataGrid } from "../components/striped-data-grid";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import { DateTime } from "luxon";
import { ErrorContext } from "../components/app-routes";
import { useLocation, useHistory } from "react-router-dom";
import { MobileDatePicker } from "@mui/x-date-pickers";

type MushroomStage =
  | "agar"
  | "liquid_culture"
  | "grain_spawn"
  | "bulk_spawn"
  | "fruiting_block";

type MushroomStatus =
  | "incubating"
  | "casing"
  | "fruiting"
  | "cold_hold"
  | "harvest"
  | "completed";

type MushroomTaskRow = MushroomTask & {
  completed: boolean;
};

function MushroomTasksDaySection(props: {
  title: string;
  rows: MushroomTaskRow[];
  columns: GridColDef<MushroomTaskRow>[];
}) {
  if (!props.rows.length) return null;

  // a nice auto height-ish feel like SeedLeaf sections
  const height = Math.min(620, 140 + props.rows.length * 52);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mb: 1.25,
          fontWeight: 400,
          color: "primary.main", // theme blue
        }}
      >
        {props.title}
      </Typography>

      <Box sx={{ height, width: "100%" }}>
        <StripedDataGrid
          rows={props.rows}
          columns={props.columns}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
          hideFooter={props.rows.length <= 10}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 25 } },
          }}
        />
      </Box>
    </Box>
  );
}

interface MushroomBoardItem {
  id: number;
  code: string;
  strainName: string;
  stage: MushroomStage;
  status: MushroomStatus;
  roomName: string | null;
  inoculatedAt: string;
  notes: string | null;
  archivedAt: string | null;
}

type MushroomTaskType =
  | "inoculate"
  | "shake"
  | "mix_spawn"
  | "move_to_fruiting"
  | "casing"
  | "harvest"
  | "package";

interface MushroomTask {
  id: string;
  date: string; // yyyy-LL-dd
  type: MushroomTaskType;
  title: string;
  code: string;
  strainName: string;
  roomName: string | null;
}

const mushroomBoardStorageKey = "mushroomBoardItems";
const mushroomStrainsStorageKey = "mushroomStrains";
const mushroomCompletedTasksStorageKey = "mushroomCompletedTasks";

interface MushroomCompletedTask {
  taskId: string;
  datePlanned: string; // yyyy-LL-dd
  dateActual: string; // yyyy-LL-dd
  completedBy: string;
  notes: string;
  harvestWeight?: number | null;
}

const defaultTiming = {
  daysAgarPlate: 14,
  daysLiquidCulture: 10,
  daysGrainSpawn: 0,
  daysBulkSpawn: 0,
  daysFruitingBlockColonization: 14,
  daysCasing: 0,
  daysFruitingToFirstHarvest: 7,
  harvestWindowDays: 5,
};

const getTimingForStrainName = (strains: any[], name: string) => {
  const found = strains.find(
    (s) => (s?.name || "").toLowerCase() === (name || "").trim().toLowerCase(),
  );

  if (found) {
    return {
      daysAgarPlate:
        found.daysAgarPlate ??
        found.daysAgarToGrain ??
        defaultTiming.daysAgarPlate,
      daysLiquidCulture:
        found.daysLiquidCulture ??
        found.daysLCToGrain ??
        defaultTiming.daysLiquidCulture,
      daysGrainSpawn:
        found.daysGrainSpawn ??
        found.daysGrainToBulk ??
        defaultTiming.daysGrainSpawn,
      daysBulkSpawn: found.daysBulkSpawn ?? defaultTiming.daysBulkSpawn,
      daysFruitingBlockColonization:
        found.daysFruitingBlockColonization ??
        found.daysSpawnToBlock ??
        defaultTiming.daysFruitingBlockColonization,
      daysCasing: found.daysCasing ?? defaultTiming.daysCasing,
      daysFruitingToFirstHarvest:
        found.daysFruitingToFirstHarvest ??
        found.daysFruitingToHarvest ??
        defaultTiming.daysFruitingToFirstHarvest,
      harvestWindowDays:
        found.harvestWindowDays ?? defaultTiming.harvestWindowDays,
    };
  }

  return defaultTiming;
};

const estimateFirstHarvestRange = (
  strains: any[],
  strainName: string,
  stage: MushroomStage,
  inoculatedAt: string,
) => {
  const t = getTimingForStrainName(strains, strainName);

  const d = DateTime.fromISO(inoculatedAt);
  if (!d.isValid) {
    return { start: null as DateTime | null, end: null as DateTime | null };
  }

  const daysFromStage =
    stage === "agar"
      ? t.daysAgarPlate +
        t.daysGrainSpawn +
        t.daysBulkSpawn +
        t.daysFruitingBlockColonization +
        t.daysCasing +
        t.daysFruitingToFirstHarvest
      : stage === "liquid_culture"
        ? t.daysLiquidCulture +
          t.daysGrainSpawn +
          t.daysBulkSpawn +
          t.daysFruitingBlockColonization +
          t.daysCasing +
          t.daysFruitingToFirstHarvest
        : stage === "grain_spawn"
          ? t.daysGrainSpawn +
            t.daysBulkSpawn +
            t.daysFruitingBlockColonization +
            t.daysCasing +
            t.daysFruitingToFirstHarvest
          : stage === "bulk_spawn"
            ? t.daysBulkSpawn +
              t.daysFruitingBlockColonization +
              t.daysCasing +
              t.daysFruitingToFirstHarvest
            : t.daysFruitingBlockColonization +
              t.daysCasing +
              t.daysFruitingToFirstHarvest;

  const start = d.plus({ days: daysFromStage });
  const end = start.plus({ days: t.harvestWindowDays });

  return { start, end };
};

const estimateMoveToFruitingDate = (
  strains: any[],
  item: MushroomBoardItem,
) => {
  // MVP: only meaningful for fruiting blocks in incubation.
  // Move-to-fruiting date = inoculatedAt + Spawn→Block days (i.e., colonization time)
  const t = getTimingForStrainName(strains, item.strainName);
  const d = DateTime.fromISO(item.inoculatedAt);
  if (!d.isValid) return null;

  if (item.stage !== "fruiting_block") return null;

  return d.plus({ days: t.daysFruitingBlockColonization });
};

export default function MushroomTasksPage() {
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  useEffect(() => {
    const dateParam = searchParams.get("date");

    if (dateParam) return;

    const updated = new URLSearchParams(searchParams);
    updated.set("date", DateTime.local().toFormat("yyyy-LL-dd"));

    history.replace({ search: updated.toString() });
  }, [searchParams, history]);
  const { handleError } = useContext(ErrorContext);

  const [busy, setBusy] = useState(false);
  const [boardItems, setBoardItems] = useState<MushroomBoardItem[]>([]);
  const [strains, setStrains] = useState<any[]>([]);
  const [completedTasks, setCompletedTasks] = useState<{
    [taskId: string]: MushroomCompletedTask;
  }>({});

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTask, setCompleteTask] = useState<MushroomTask | null>(null);
  const [completeDateActual, setCompleteDateActual] = useState(
    DateTime.local().toFormat("yyyy-LL-dd"),
  );
  const [completeCompletedBy, setCompleteCompletedBy] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [completeHarvestWeight, setCompleteHarvestWeight] = useState<
    number | ""
  >("");

  const selectedDate =
    searchParams.get("date") ?? DateTime.local().toFormat("yyyy-LL-dd");

  const setSelectedDate = (d: string) => {
    const updated = new URLSearchParams(searchParams);
    updated.set("date", d);
    history.replace({ search: updated.toString() });
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(mushroomBoardStorageKey);
      if (raw) {
        setBoardItems(JSON.parse(raw) as MushroomBoardItem[]);
      } else {
        setBoardItems([]);
      }
    } catch (error) {
      handleError(error);
      setBoardItems([]);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(mushroomStrainsStorageKey);
      if (raw) {
        setStrains(JSON.parse(raw) as any[]);
      } else {
        setStrains([]);
      }
    } catch (error) {
      handleError(error);
      setStrains([]);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(mushroomCompletedTasksStorageKey);
      if (raw) {
        setCompletedTasks(JSON.parse(raw) as any);
      } else {
        setCompletedTasks({});
      }
    } catch (error) {
      handleError(error);
      setCompletedTasks({});
    }
  }, []);

  const tasksForDay = useMemo(() => {
    const day = DateTime.fromFormat(selectedDate, "yyyy-LL-dd");
    if (!day.isValid) return [];

    const dayKey = day.toFormat("yyyy-LL-dd");

    const tasks: MushroomTask[] = [];

    const activeItems = (boardItems || []).filter((i) => i.archivedAt === null);

    for (let i = 0; i < activeItems.length; i++) {
      const item = activeItems[i];

      // 1) Move to Fruiting (only for incubating fruiting blocks)
      if (item.status === "incubating") {
        const moveDate = estimateMoveToFruitingDate(strains, item);
        if (moveDate && moveDate.toFormat("yyyy-LL-dd") === dayKey) {
          tasks.push({
            id: `move_to_fruiting:${item.id}:${dayKey}`,
            date: dayKey,
            type: "move_to_fruiting",
            title: "Move to Fruiting",
            code: item.code,
            strainName: item.strainName,
            roomName: item.roomName,
          });
        }
      }

      // 2) Harvest (only for fruiting batches, daily during harvest window)
      if (item.status === "fruiting" || item.status === "harvest") {
        const range = estimateFirstHarvestRange(
          strains,
          item.strainName,
          item.stage,
          item.inoculatedAt,
        );

        if (range.start && range.end) {
          const startKey = range.start.toFormat("yyyy-LL-dd");
          const endKey = range.end.toFormat("yyyy-LL-dd");

          if (dayKey >= startKey && dayKey <= endKey) {
            // Harvest task
            tasks.push({
              id: `harvest:${item.id}:${dayKey}`,
              date: dayKey,
              type: "harvest",
              title: "Harvest",
              code: item.code,
              strainName: item.strainName,
              roomName: item.roomName,
            });

            // Package task (same day as harvest)
            tasks.push({
              id: `package:${item.id}:${dayKey}`,
              date: dayKey,
              type: "package",
              title: "Package",
              code: item.code,
              strainName: item.strainName,
              roomName: item.roomName,
            });
          }
        }
      }
    }

    const priority: Record<MushroomTaskType, number> = {
      move_to_fruiting: 10,
      harvest: 20,
      package: 30,

      // not used yet in UI, but keep stable ordering if you add later
      inoculate: 50,
      shake: 50,
      mix_spawn: 50,
      casing: 50,
    };

    tasks.sort((a, b) => (priority[a.type] ?? 999) - (priority[b.type] ?? 999));

    return tasks;
  }, [boardItems, selectedDate, strains]);

  const rows: MushroomTaskRow[] = tasksForDay.map((t) => ({
    ...t,
    completed: !!completedTasks[t.id],
  }));

  const moveRows = rows.filter((r) => r.type === "move_to_fruiting");
  const harvestRows = rows.filter((r) => r.type === "harvest");
  const packageRows = rows.filter((r) => r.type === "package"); // for later when you add it

  const baseColumns: GridColDef<MushroomTaskRow>[] = [
    {
      field: "completed",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<MushroomTaskRow, boolean>) => (
        <Checkbox
          checked={!!params.row.completed}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={() => toggleComplete(params.row)}
        />
      ),
    },
    {
      field: "code",
      headerName: "Code",
      width: 140,
      sortable: true,
    },
    {
      field: "strainName",
      headerName: "Strain",
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "roomName",
      headerName: "Room",
      flex: 1,
      minWidth: 160,
      sortable: true,
      valueGetter: (value, row) => row.roomName ?? "",
    },
  ];

  const selectedLuxon = DateTime.fromFormat(selectedDate, "yyyy-LL-dd");

  const handlePrevDay = () => {
    if (!selectedLuxon.isValid) return;
    setSelectedDate(selectedLuxon.minus({ days: 1 }).toFormat("yyyy-LL-dd"));
  };

  const handleNextDay = () => {
    if (!selectedLuxon.isValid) return;
    setSelectedDate(selectedLuxon.plus({ days: 1 }).toFormat("yyyy-LL-dd"));
  };

  const openCompleteDialog = (t: MushroomTask) => {
    setCompleteTask(t);

    const existing = completedTasks[t.id];

    setCompleteDateActual(
      existing?.dateActual ||
        DateTime.fromFormat(t.date, "yyyy-LL-dd").toFormat("yyyy-LL-dd"),
    );

    setCompleteCompletedBy(existing?.completedBy || "");
    setCompleteNotes(existing?.notes || "");
    setCompleteHarvestWeight(
      typeof existing?.harvestWeight === "number" ? existing.harvestWeight : "",
    );

    setCompleteOpen(true);
  };

  const saveCompletedTasks = (next: {
    [taskId: string]: MushroomCompletedTask;
  }) => {
    setCompletedTasks(next);
    window.localStorage.setItem(
      mushroomCompletedTasksStorageKey,
      JSON.stringify(next),
    );
  };

  const toggleComplete = (t: MushroomTask) => {
    const existing = completedTasks[t.id];

    if (existing) {
      const ok = window.confirm("Uncomplete this task?");
      if (!ok) return;

      const next = { ...completedTasks };
      delete next[t.id];
      saveCompletedTasks(next);
      return;
    }

    openCompleteDialog(t);
  };

  const handleSaveComplete = () => {
    if (!completeTask) return;

    const record: MushroomCompletedTask = {
      taskId: completeTask.id,
      datePlanned: completeTask.date,
      dateActual: completeDateActual,
      completedBy: completeCompletedBy,
      notes: completeNotes,
      harvestWeight:
        completeTask.type === "harvest"
          ? completeHarvestWeight === ""
            ? null
            : Number(completeHarvestWeight)
          : null,
    };

    const next = {
      ...completedTasks,
      [completeTask.id]: record,
    };

    saveCompletedTasks(next);
    setCompleteOpen(false);
  };

  return (
    <Page title="Tasks">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={handlePrevDay} size="large">
          <ChevronLeftIcon />
        </IconButton>

        <MobileDatePicker
          closeOnSelect
          format="LLL d, yyyy"
          value={DateTime.fromISO(selectedDate)}
          onChange={(date) => {
            if (date && date.isValid) {
              setSelectedDate(date.toISODate()!);
            }
          }}
          slotProps={{
            toolbar: { hidden: true },
            textField: {
              label: "Day",
              size: "small",
              sx: { width: 190 },
              inputProps: { readOnly: true },
            },
          }}
        />

        <IconButton onClick={handleNextDay} size="large">
          <ChevronRightIcon />
        </IconButton>

        <Button
          variant="outlined"
          onClick={() => {
            const today = DateTime.local().toISODate();

            setSelectedDate(
              today ? today : DateTime.local().toFormat("yyyy-LL-dd"),
            );
          }}
          disabled={busy}
        >
          Today
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            return;
          }}
          disabled={busy}
        >
          Refresh
        </Button>
      </Box>

      {moveRows.length === 0 &&
      harvestRows.length === 0 &&
      packageRows.length === 0 ? (
        <Box sx={{ opacity: 0.75 }}>No production tasks for this day.</Box>
      ) : (
        <>
          <MushroomTasksDaySection
            title="Move to Fruiting"
            rows={moveRows}
            columns={baseColumns}
          />
          <MushroomTasksDaySection
            title="Harvest"
            rows={harvestRows}
            columns={baseColumns}
          />
          <MushroomTasksDaySection
            title="Package"
            rows={packageRows}
            columns={baseColumns}
          />
        </>
      )}

      <Dialog
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {completeTask
            ? `Complete Task: ${completeTask.title}`
            : "Complete Task"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            {completeTask && (
              <Box sx={{ opacity: 0.85, fontSize: 14 }}>
                Planned:{" "}
                {DateTime.fromFormat(completeTask.date, "yyyy-LL-dd").toFormat(
                  "LLL dd, yyyy",
                )}
              </Box>
            )}
            <MobileDatePicker
              closeOnSelect
              format="LLL d, yyyy"
              value={DateTime.fromISO(completeDateActual)}
              onChange={(date) => {
                if (date && date.isValid) {
                  setCompleteDateActual(date.toISODate()!);
                }
              }}
              slotProps={{
                toolbar: { hidden: true },
                textField: {
                  label: "Date Completed",
                  fullWidth: true,
                  inputProps: { readOnly: true },
                },
              }}
            />

            <TextField
              label="Completed By"
              value={completeCompletedBy}
              onChange={(e) => setCompleteCompletedBy(e.target.value)}
              fullWidth
            />

            {completeTask?.type === "harvest" && (
              <TextField
                label="Harvest Weight"
                type="number"
                value={completeHarvestWeight}
                onChange={(e) =>
                  setCompleteHarvestWeight(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                fullWidth
              />
            )}

            <TextField
              label="Notes"
              value={completeNotes}
              onChange={(e) => setCompleteNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveComplete}
            variant="contained"
            color="primary"
            disabled={busy || !completeTask}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
